import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  GoogleCredentialResponse,
  LoginAuthenticationResponse,
} from "@medplum/core";
import { normalizeErrorString } from "@medplum/core";
import { captureException } from "@sentry/react";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { MEDPLUM_LOGINS_STORAGE_KEY } from "@/constants/medplum.constants";
import { ROUTES } from "@/constants/routes.constants";
import { medplumClient } from "@/lib/medplum";
import { getReferenceId } from "@/lib/utils/helpers";
import {
  activeProjectCache,
  membershipsCache,
} from "@/lib/utils/helpers/auth-cache";
import { selectLoginProfile } from "@/apis/medplum";
import { useLoginProcessing } from "@/context/login-provider";

import {
  Button,
  Card,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  PasswordInput,
} from "@/components/base";
import { GoogleSignInSection } from "@/components/module/GoogleSignInSection";

const formSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

/**
 * A practitioner belongs to one project per clinic. If this threshold is
 * exceeded we capture a Sentry exception to track how common the case is.
 * If it turns out to be frequent, the sequential login-per-membership flow
 * needs to be revisited (e.g. rate limits, UX delays).
 */
const MAX_MEMBERSHIPS = 3;

/**
 * The `offline` scope is required for the server to issue a refresh token —
 * without it the session dies the moment the 1h access token expires and the
 * user is bounced to login. `openid` is included so the ID token is returned.
 */
const LOGIN_SCOPE = "openid offline";

type FormValues = z.infer<typeof formSchema>;

/**
 * Replaces the Medplum-managed logins array in localStorage with only the
 * current active login. Called when a super admin session is detected so that
 * residual non-admin sessions from the same login flow are not persisted.
 * Direct localStorage access is required because MedplumClient.storage is private.
 */
const clearNonSuperAdminLogins = () => {
  const activeLogin = medplumClient.getActiveLogin();

  localStorage.setItem(
    MEDPLUM_LOGINS_STORAGE_KEY,
    JSON.stringify(activeLogin ? [activeLogin] : [])
  );
};

const authorizeMembership = async (
  loginToken: string,
  membershipId: string
) => {
  const { code } = await selectLoginProfile(loginToken, membershipId);

  if (!code) {
    throw new Error("No code found while authenticating");
  }

  await medplumClient.processCode(code);
};

const processLoginResponse = async (
  queryClient: QueryClient,
  { login, memberships, code }: LoginAuthenticationResponse,
  nextLogin: () => Promise<string>
): Promise<void> => {
  /**
   * Clear before any of this response makes a login active. Logging out is a
   * client-side navigation, so whatever the previous login cached is still in
   * this tab, and every key carries the project at most — never the login.
   * Clearing here covers every way in, including ones added later.
   */
  queryClient.clear();

  if (!memberships?.length) {
    membershipsCache.clear();

    if (!code) {
      throw new Error("No code found while authenticating");
    }

    await medplumClient.processCode(code);
    return;
  }

  if (memberships.length > MAX_MEMBERSHIPS) {
    captureException(
      new Error(`Unexpected membership count: ${memberships.length}`)
    );
  }

  const authorizedMemberships: typeof memberships = [];

  for (const [index, membership] of memberships.entries()) {
    const { id: membershipId } = membership;

    if (!membershipId) {
      continue;
    }

    const token = index === 0 ? login : await nextLogin();

    try {
      await authorizeMembership(token, membershipId);

      if (medplumClient.isSuperAdmin()) {
        clearNonSuperAdminLogins();
        return;
      }

      authorizedMemberships.push(membership);
    } catch {
      /**
       * Skip memberships whose profile is not active — continue authorizing
       * remaining memberships.
       */
    }
  }

  if (!authorizedMemberships.length) {
    membershipsCache.clear();

    const error = new Error("No active memberships could be authorized");

    captureException(error);

    throw error;
  }

  membershipsCache.set(authorizedMemberships);

  const cachedProjectId = activeProjectCache.get();
  const cachedMembership = cachedProjectId
    ? authorizedMemberships.find(
        ({ project: { reference } }) =>
          getReferenceId(reference, "Project") === cachedProjectId
      )
    : null;

  const logins = medplumClient.getLogins();
  const cachedLogin = cachedMembership
    ? logins.find(
        ({ project: { reference } }) =>
          reference === cachedMembership.project.reference
      )
    : null;

  const targetLogin = cachedLogin ?? medplumClient.getActiveLogin();
  const targetMembership =
    cachedMembership ??
    authorizedMemberships.find(
      ({ project: { reference } }) =>
        reference === targetLogin?.project.reference
    );

  if (targetLogin) {
    const targetProjectId = getReferenceId(
      targetMembership?.project.reference,
      "Project"
    );

    if (targetProjectId) {
      activeProjectCache.set(targetProjectId);
    }

    await medplumClient.setActiveLogin(targetLogin);
  }
};

export const LoginForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setLoginProcessing } = useLoginProcessing();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
  });

  const handleSubmit = async ({ email, password }: FormValues) => {
    setLoginProcessing(true);

    const payload = { email, password, scope: LOGIN_SCOPE };

    try {
      const response = await medplumClient.startLogin(payload);
      /**
       * Each startLogin overwrites the PKCE codeVerifier, so we must exchange
       * the first code before calling startLogin again for remaining memberships.
       */
      await processLoginResponse(queryClient, response, async () => {
        const { login } = await medplumClient.startLogin(payload);
        return login;
      });
    } catch (error) {
      toast.error(normalizeErrorString(error));
    } finally {
      setLoginProcessing(false);
    }
  };

  const handleGoogleCredential = useCallback(
    async (response: GoogleCredentialResponse) => {
      setLoginProcessing(true);

      const payload = {
        googleClientId: response.clientId,
        googleCredential: response.credential,
        scope: LOGIN_SCOPE,
      };

      try {
        const authResponse = await medplumClient.startGoogleLogin(payload);
        await processLoginResponse(queryClient, authResponse, async () => {
          const { login } = await medplumClient.startGoogleLogin(payload);
          return login;
        });
      } catch (error) {
        toast.error(normalizeErrorString(error));
      } finally {
        setLoginProcessing(false);
      }
    },
    [queryClient, setLoginProcessing]
  );

  return (
    <Card
      padding="lg"
      className="flex w-full max-w-md -translate-y-1/6 flex-col gap-6"
    >
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-3xl font-semibold">{t("Sign in")}</h1>
      </div>
      <GoogleSignInSection onCredential={handleGoogleCredential} />
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit(handleSubmit)(event);
          }}
          className="flex flex-col gap-4"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("Email")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t("you@example.com")}
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>{t("Password")}</FormLabel>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground cursor-pointer text-sm underline-offset-4 transition-colors hover:underline"
                    onClick={() =>
                      navigate(ROUTES.RESET_PASSWORD, {
                        state: { email: form.getValues("email") },
                      })
                    }
                  >
                    {t("Forgot password?")}
                  </button>
                </div>
                <FormControl>
                  <PasswordInput
                    placeholder="••••••••"
                    autoComplete="current-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            variant="primary"
            className="w-full"
            loading={form.formState.isSubmitting}
          >
            {t("Sign in")}
          </Button>
        </form>
      </Form>
    </Card>
  );
};
